import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { StandardTextFieldProps, TextField } from '@material-ui/core';

import '../translations/translations';
import { InputValidationResult } from '.';

export interface ValidatedInputProps extends StandardTextFieldProps {
  inputName: string;
  required: boolean;
  label: string;
  onChangeValidator?: (value: string) => InputValidationResult;
  onBlurValidator?: (value: string) => InputValidationResult;
}

const ValidatedTextInput: React.FC<ValidatedInputProps> = (props) => {
  const {
    inputName,
    type,
    required,
    label,
    helperText,
    onChange,
    onChangeValidator,
    onBlur,
    onBlurValidator,
    value,
    ...otherProps
  } = props;
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const { t } = useTranslation(['translations']);

  const doRequireValidation = (value: string) => {
    const requiredValidation = required ? value !== '' : true;

    if (!requiredValidation) {
      setErrorMessage(t('validation.required'));
    } else {
      setErrorMessage(undefined);
    }
  };

  const handleValidationResult = (validationResult: InputValidationResult) => {
    if (!validationResult.isValid) {
      const errorMessage =
        validationResult.errorMessage ??
        (validationResult.errorMessageKey
          ? t(
              validationResult.errorMessageKey,
              validationResult.errorMessageOptions,
            )
          : t('validation.default'));

      setErrorMessage(errorMessage);
    } else {
      setErrorMessage(undefined);
    }
  };

  const handleChange = (e: React.FocusEvent<HTMLInputElement>) => {
    const { value } = e.target;

    if (onChange) {
      onChange(e);
    }

    if (onChangeValidator) {
      const validationResult = onChangeValidator(value);
      if (validationResult) {
        handleValidationResult(validationResult);
      }
    } else {
      doRequireValidation(value);
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement>) => {
    const { value } = e.target;

    if (onBlur) {
      onBlur(e);
    }

    if (onBlurValidator) {
      const validationResult = onBlurValidator(value);
      if (validationResult) {
        handleValidationResult(validationResult);
      }
    }
  };

  useEffect(() => {
    if (onChangeValidator && !!value) {
      const validationResult = onChangeValidator(value as string);
      if (validationResult) {
        handleValidationResult(validationResult);
      }
    }
  }, [value]);

  let helperTextWithError = helperText;

  if (errorMessage && !!errorMessage) {
    helperTextWithError = errorMessage;
  }

  return (
    <>
      <TextField
        {...otherProps}
        type={type}
        variant="outlined"
        required={required}
        fullWidth
        id={inputName}
        label={label}
        name={inputName}
        helperText={helperTextWithError}
        error={!!errorMessage}
        autoComplete={type}
        onChange={handleChange}
        onBlur={handleBlur}
        value={value}
        InputLabelProps={{
          shrink: value === undefined || value === '' ? undefined : true,
        }} // Fixes a bug where autofill does not trigger the label animation
      />
    </>
  );
};

export { ValidatedTextInput };
